import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
interface AuthPayload {
  token: string;
  userId: number;
  shopId: number;
  exp: number;
}

@Injectable({
  providedIn: 'root',
})
export class AuthFragmentService {
  /**
   * Must match the iOS key in `ManagementURLHelper.swift`.
   * See `FeatherBarApp-iOS/FeatherBarApp/FeatherBarApp/Supporting Files/ManagementURLHelper.swift`.
   */
  private readonly KEY_HEX =
    environment.authFragmentKey;

  /**
   * Try to read and decrypt the `#auth=<base64url>` fragment from the URL.
   *
   * Returns the decrypted payload on success, or `null` on any failure.
   * In all cases, the URL fragment is stripped afterwards so it does not
   * remain in browser history or get copied/shared accidentally.
   */
  async tryDecryptFragment(): Promise<AuthPayload | null> {
    const hash = window.location.hash;
    if (!hash || !hash.startsWith('#auth=')) {
      return null;
    }

    const encoded = hash.substring('#auth='.length);
    const bytes = this.base64UrlDecode(encoded);
    // Need at least 12 bytes of nonce + 16 bytes of tag
    if (bytes.length < 28) {
      console.warn('Auth fragment too short to be valid');
      this.stripFragment();
      return null;
    }

    const iv = bytes.slice(0, 12);
    const ciphertextWithTag = bytes.slice(12);

    try {
      const key = await this.importKey();
      const decrypted = await crypto.subtle.decrypt(
        { name: 'AES-GCM', iv, tagLength: 128 },
        key,
        ciphertextWithTag
      );

      const json = new TextDecoder().decode(decrypted);
      const payload: AuthPayload = JSON.parse(json);

      const nowSeconds = Math.floor(Date.now() / 1000);
      if (payload.exp < nowSeconds) {
        console.warn('Auth fragment expired');
        return null;
      }

      return payload;
    } catch (e) {
      console.error('Failed to decrypt auth fragment:', e);
      return null;
    } finally {
      this.stripFragment();
    }
  }

  private async importKey(): Promise<CryptoKey> {
    const keyBytes = this.hexToBytes(this.KEY_HEX);
    // Narrow the underlying buffer to `ArrayBuffer` so it matches the `BufferSource`
    // overload expected by `crypto.subtle.importKey` in TypeScript's DOM lib.
    const keyData = keyBytes.buffer as ArrayBuffer;
    return crypto.subtle.importKey('raw', keyData, 'AES-GCM', false, ['decrypt']);
  }

  private stripFragment(): void {
    if (window.location.hash) {
      history.replaceState(
        null,
        '',
        window.location.pathname + window.location.search
      );
    }
  }

  private base64UrlDecode(str: string): Uint8Array {
    // Restore standard Base64 characters
    let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
    // Add padding
    while (base64.length % 4 !== 0) {
      base64 += '=';
    }

    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  }

  private hexToBytes(hex: string): Uint8Array {
    const len = hex.length;
    const bytes = new Uint8Array(len / 2);
    for (let i = 0; i < len; i += 2) {
      bytes[i / 2] = parseInt(hex.substring(i, i + 2), 16);
    }
    return bytes;
  }
}

